// AI Profile Generator — full-height right-side sheet, ported pixel-true
// from prototype/ai-explore-playbook.jsx (AIGeneratorSheet).
//
// Three input modes (Describe · From URL · Upload JSON), a "thinking"
// phase with an animated checklist, and a preview phase with a Document
// view (editable draft) + JSON view, a refine bar, and three save paths
// in the footer (Save draft · Use in Flight Plan · Review & edit).
//
// State stays local to the sheet — only the final user action mutates
// the build config / navigates / fires a toast.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUiStore } from "@/lib/store/uiStore";
import { useConfigStore } from "@/lib/store/configStore";
import { aiApi, type PackJson } from "@/lib/api/ai";

type InputMode = "describe" | "url" | "upload";
type Phase = "input" | "thinking" | "preview";
type ViewMode = "document" | "json";
type StoryFit = "perfect" | "good" | "custom";

// Map any value the model returns for a story's `match` field down to
// our 3 canonical buckets. Belt for the parser; suspenders for the
// renderer. Keep this list permissive — when in doubt, "good" is the
// safest visual default (neutral chip, no false promise of a perfect
// fit, no implication that the AI "made it up" either).
const STORY_FIT_SYNONYMS: Record<string, StoryFit> = {
  perfect: "perfect",
  excellent: "perfect",
  great: "perfect",
  high: "perfect",
  strong: "perfect",
  good: "good",
  ok: "good",
  fine: "good",
  medium: "good",
  moderate: "good",
  fair: "good",
  weak: "good",
  low: "good",
  custom: "custom",
  novel: "custom",
  new: "custom",
  generated: "custom",
  "ai-generated": "custom",
};

function normalizeStoryFit(raw: unknown): StoryFit {
  if (typeof raw !== "string") return "good";
  return STORY_FIT_SYNONYMS[raw.trim().toLowerCase()] ?? "good";
}

interface ArchetypeEntry {
  name: string;
  label: string;
  description: string;
  basedOn: string;
  weight: number;
}

interface ArchetypeRoster {
  accounts: ArchetypeEntry[];
  reps: ArchetypeEntry[];
  agents: ArchetypeEntry[];
  contactTitles: string[];
  // Cross-reference health: are all team[].archetype refs in the
  // matching archetype list? If not, the data generators won't honor
  // the AI's flavoring at run time and we surface a warning.
  orphanRepRefs: string[];
  orphanAgentRefs: string[];
}

interface CaseCategoryEntry {
  name: string;
  label: string;
  description: string;
  caseType: string;
  subjects: string[];
}

interface DraftStory {
  id: string;
  title: string;
  description?: string;
  fit: StoryFit;
  layer: "data" | "insight" | "activation";
}

interface VolumeTier {
  cases: number;
  opportunities: number;
  leads: number;
  total: number;
}

interface RefineEntry {
  at: string;
  text: string;
}

interface ProfileDraft {
  name: string;
  industry: string;
  tagline: string;
  brandColor: string;
  icon: string;
  logoSource: string;
  description: string;
  archetypes: ArchetypeRoster;
  caseCategories: CaseCategoryEntry[];
  products: string[];
  stories: DraftStory[];
  volumes: Record<string, VolumeTier>;
  warnings: string[];
  refineLog?: RefineEntry[];
}

const CANONICAL_REP_LABELS: Record<string, string> = {
  veteran_closer: "Veteran Closer",
  consistent_performer: "Consistent Performer",
  ramping_new_hire: "Ramping New Hire",
  struggling_rep: "Struggling Rep",
  enterprise_specialist: "Enterprise Specialist",
};
const CANONICAL_AGENT_LABELS: Record<string, string> = {
  star_agent: "Star Agent",
  solid_performer: "Solid Performer",
  developing_agent: "Developing Agent",
  struggling_agent: "Struggling Agent",
  specialist: "Specialist",
};
const CANONICAL_ACCOUNT_LABELS: Record<string, string> = {
  growth_engine: "Growth Engine",
  steady_partner: "Steady Partner",
  emerging_prospect: "Emerging Prospect",
  at_risk: "At Risk",
  whale_prospect: "Whale Prospect",
  dormant: "Dormant",
};

function canonicalLabel(canonical: string, kind: "rep" | "agent" | "account"): string {
  const map =
    kind === "rep" ? CANONICAL_REP_LABELS :
    kind === "agent" ? CANONICAL_AGENT_LABELS : CANONICAL_ACCOUNT_LABELS;
  return map[canonical] || canonical.replace(/_/g, " ");
}

function parseArchetypeList(raw: unknown): ArchetypeEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ArchetypeEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const name = typeof obj.name === "string" ? obj.name : "";
    if (!name) continue;
    const basedOn = typeof obj.based_on === "string" ? obj.based_on : "";
    const weight = typeof obj.weight === "number" ? obj.weight : 0;
    out.push({
      name,
      label: typeof obj.label === "string" && obj.label
        ? obj.label
        : name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: typeof obj.description === "string" ? obj.description : "",
      basedOn,
      weight,
    });
  }
  return out;
}

function parseCaseCategories(raw: unknown): CaseCategoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: CaseCategoryEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const obj = item as Record<string, unknown>;
    const name = typeof obj.name === "string" ? obj.name : "";
    if (!name) continue;
    const subjectsRaw = obj.subjects;
    const subjects = Array.isArray(subjectsRaw)
      ? (subjectsRaw.filter((s) => typeof s === "string") as string[])
      : [];
    out.push({
      name,
      label: typeof obj.label === "string" && obj.label
        ? obj.label
        : name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: typeof obj.description === "string" ? obj.description : "",
      caseType: typeof obj.case_type === "string" ? obj.case_type : "Other",
      subjects,
    });
  }
  return out;
}

// Friendly titles for the 16 canonical story IDs the AI emits in
// stories.defaults / stories.descriptions. Falls back to a generic
// title-case conversion when an unknown ID slips through.
const STORY_TITLE_OVERRIDES: Record<string, string> = {
  pipeline_health_crisis: "Pipeline Health Crisis",
  rep_ramp_trajectory: "Rep Ramp Trajectory",
  product_mix_shift: "Product Mix Shift",
  lead_source_roi: "Lead Source ROI",
  regional_performance_gap: "Regional Performance Gap",
  forecast_accuracy: "Forecast Accuracy",
  account_turnaround: "Account Turnaround",
  satisfaction_transformation: "Satisfaction Transformation",
  agent_performance_spotlight: "Agent Performance Spotlight",
  regional_service_quality: "Regional Service Quality",
  response_time_satisfaction: "Response Time vs Satisfaction",
  omni_channel_optimization: "Omni-Channel Optimization",
  knowledge_base_roi: "Knowledge Base ROI",
  product_support_excellence: "Product Support Excellence",
  sla_compliance_journey: "SLA Compliance Journey",
  premium_support_roi: "Premium Support ROI",
};

function storyIdToTitle(id: string): string {
  if (STORY_TITLE_OVERRIDES[id]) return STORY_TITLE_OVERRIDES[id];
  return id
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

function inferStoryLayer(id: string): DraftStory["layer"] {
  // Cheap heuristic: service stories cluster on insight, sales activation
  // stories on activation, everything else is data. Used only for the
  // little chip color on the card.
  const lower = id.toLowerCase();
  if (lower.includes("satisfaction") || lower.includes("agent") || lower.includes("knowledge")) {
    return "insight";
  }
  if (lower.includes("turnaround") || lower.includes("ramp") || lower.includes("compliance")) {
    return "activation";
  }
  return "data";
}

function ArchetypeSection({
  title,
  entries,
  kind,
  orphans = [],
}: {
  title: string;
  entries: ArchetypeEntry[];
  kind: "rep" | "agent" | "account";
  orphans?: string[];
}): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--eb-50)",
          textTransform: "uppercase",
          letterSpacing: ".06em",
          marginBottom: 6,
        }}
      >
        {title} ({entries.length})
        {orphans.length > 0 && (
          <span
            style={{
              marginLeft: 8,
              color: "var(--warn, #B45309)",
              textTransform: "none",
              fontWeight: 500,
              letterSpacing: 0,
            }}
          >
            · {orphans.length} orphan ref{orphans.length === 1 ? "" : "s"} from team roster
          </span>
        )}
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
          No archetypes defined — pipeline will use generic defaults.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 6,
          }}
        >
          {entries.map((e) => (
            <div
              key={e.name}
              style={{
                padding: 10,
                background: "white",
                border: "1px solid var(--bd)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 12, color: "var(--ink)", fontWeight: 600 }}>
                  {e.label}
                </span>
                {e.weight > 0 && (
                  <span style={{ fontSize: 10, color: "var(--ink-3)" }}>
                    {Math.round(e.weight * 100)}%
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  marginTop: 2,
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                }}
              >
                based on {canonicalLabel(e.basedOn, kind)}
              </div>
              {e.description && (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-2, var(--ink))",
                    marginTop: 5,
                    lineHeight: 1.4,
                  }}
                >
                  {e.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function asNumber(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function normalizeVolumes(raw: unknown): Record<string, VolumeTier> {
  // The pack ships volumes as { small: { cases, opportunities, leads }, ... }
  // but legacy drafts (and one early-draft escape hatch below) ship a flat
  // { cases: N, opportunities: N } at the top level. Tolerate both — an
  // unrecognized shape just yields zeros, which renders cleanly as "0".
  const out: Record<string, VolumeTier> = {};
  if (!raw || typeof raw !== "object") return out;

  const entries = Object.entries(raw as Record<string, unknown>);
  const looksLikeFlat = entries.every(([, v]) => typeof v === "number");
  if (looksLikeFlat) {
    const tier: VolumeTier = {
      cases: asNumber((raw as Record<string, unknown>).cases),
      opportunities: asNumber((raw as Record<string, unknown>).opportunities),
      leads: asNumber((raw as Record<string, unknown>).leads),
      total: 0,
    };
    tier.total = tier.cases + tier.opportunities + tier.leads;
    out.medium = tier;
    return out;
  }

  for (const [tierName, tierValue] of entries) {
    if (!tierValue || typeof tierValue !== "object") continue;
    const t = tierValue as Record<string, unknown>;
    const tier: VolumeTier = {
      cases: asNumber(t.cases),
      opportunities: asNumber(t.opportunities),
      leads: asNumber(t.leads),
      total: 0,
    };
    tier.total = tier.cases + tier.opportunities + tier.leads;
    out[tierName] = tier;
  }
  return out;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

const SAMPLES = [
  "A mid-size solar installer in Arizona — residential + commercial, ~200 reps, CEO is tech-forward",
  "Boutique cybersecurity firm selling into FinServ. 40 people, founder-led sales",
  "Regional hospital system — 8 locations, focus on patient intake and bed management",
];

const URL_SAMPLES = [
  "https://heliossolar.com",
  "https://meridian-health.org",
  "https://nightfall.ai",
];

function prettyHost(raw: string): string {
  if (!raw) return "";
  try {
    const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return u.host.replace(/^www\./, "");
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/\/$/, "");
  }
}

function thinkingStepsFor(
  mode: InputMode,
  context: { url?: string; fileName?: string },
): string[] {
  if (mode === "url") {
    const host = prettyHost(context.url ?? "") || "the site";
    return [
      `Fetching page · ${host}`,
      "Extracting company info · products · team",
      "Looking up logo · brand colors",
      "Matching industry taxonomy",
      "Generating personas",
      "Matching demo stories",
      "Estimating data volumes",
    ];
  }
  if (mode === "upload") {
    const name = context.fileName || "file";
    return [
      `Parsing ${name}`,
      "Validating schema",
      "Enriching with industry defaults",
      "Looking up logo",
      "Matching demo stories",
    ];
  }
  return [
    "Parsed prompt",
    "Matched industry taxonomy",
    "Built persona set",
    "Matching demo stories",
    "Estimating volumes",
  ];
}

function packToDraft(pack: PackJson, mode: InputMode): ProfileDraft {
  const company = (pack.company ?? {}) as Record<string, unknown>;
  const products = Array.isArray(pack.products) ? pack.products : [];
  const storiesRaw = (pack.stories as Record<string, unknown>) ?? {};

  const productNames = products
    .map((p) => {
      if (typeof p === "string") return p;
      const obj = p as Record<string, unknown>;
      return (obj.name as string) || (obj.title as string) || "";
    })
    .filter(Boolean);

  // Industry archetypes — the pack's crown-jewel cross-reference.
  // Each entry's `based_on` ties the AI's flavored name back to one of
  // the canonical 5/5/6 enums in archetypes.py, so the data generators
  // can use the right behavioral profile while preserving the
  // industry-flavored label in the UI.
  const accountArchetypes = parseArchetypeList(pack.account_archetypes);
  const repArchetypes = parseArchetypeList(pack.rep_archetypes);
  const agentArchetypes = parseArchetypeList(pack.agent_archetypes);
  const caseCategories = parseCaseCategories(pack.case_categories);

  const repNames = new Set(repArchetypes.map((a) => a.name));
  const agentNames = new Set(agentArchetypes.map((a) => a.name));
  const team = (pack.team as Record<string, unknown>) ?? {};
  const reps = Array.isArray(team.reps) ? (team.reps as Array<Record<string, unknown>>) : [];
  const agents = Array.isArray(team.agents) ? (team.agents as Array<Record<string, unknown>>) : [];
  const orphanRepRefs = Array.from(new Set(
    reps
      .map((r) => (typeof r.archetype === "string" ? r.archetype : ""))
      .filter((a) => a && !repNames.has(a))
  ));
  const orphanAgentRefs = Array.from(new Set(
    agents
      .map((a) => (typeof a.archetype === "string" ? a.archetype : ""))
      .filter((a) => a && !agentNames.has(a))
  ));

  const naming = (pack.account_naming as Record<string, unknown>) ?? {};
  const contactTitlesRaw = naming.contact_titles;
  const contactTitles = Array.isArray(contactTitlesRaw)
    ? (contactTitlesRaw.filter((t) => typeof t === "string") as string[])
    : [];

  const archetypes: ArchetypeRoster = {
    accounts: accountArchetypes,
    reps: repArchetypes,
    agents: agentArchetypes,
    contactTitles,
    orphanRepRefs,
    orphanAgentRefs,
  };

  // Stories now ship as { defaults: [story_id, ...], descriptions: { story_id: "..." } }
  // (the canonical AI pack shape since 2026-04). The previous parser
  // expected legacy { data: [{title, name}], insight: [...] } and
  // walked away with "Story 1, Story 2, ..." — every time. Read the
  // new shape first, then fall back to the legacy walker for anything
  // a hand-edited pack might still have.
  const storyEntries: DraftStory[] = [];
  const seen = new Set<string>();

  const defaults = Array.isArray(storiesRaw.defaults)
    ? (storiesRaw.defaults as unknown[])
    : [];
  const descriptions =
    storiesRaw.descriptions && typeof storiesRaw.descriptions === "object"
      ? (storiesRaw.descriptions as Record<string, unknown>)
      : {};

  for (const raw of defaults) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const id = raw.trim();
    if (seen.has(id)) continue;
    seen.add(id);
    const desc = descriptions[id];
    storyEntries.push({
      id,
      title: storyIdToTitle(id),
      description: typeof desc === "string" ? desc : undefined,
      fit: "good",
      layer: inferStoryLayer(id),
    });
  }

  // Legacy fallback: tolerate { data: [{title, match}], insight: [...] }
  // shaped packs (and any future arrays of objects). Skip the new
  // canonical keys we already consumed above.
  for (const [layerKey, items] of Object.entries(storiesRaw)) {
    if (layerKey === "defaults" || layerKey === "descriptions") continue;
    if (!Array.isArray(items)) continue;
    const layer: DraftStory["layer"] =
      layerKey.toLowerCase().includes("data")
        ? "data"
        : layerKey.toLowerCase().includes("activ")
          ? "activation"
          : "insight";
    items.slice(0, 4).forEach((s, i) => {
      const obj = (s ?? {}) as Record<string, unknown>;
      const id = `${layerKey}-${i}`;
      if (seen.has(id)) return;
      seen.add(id);
      const explicitId = typeof obj.id === "string" ? obj.id : "";
      const fallbackTitle = explicitId
        ? storyIdToTitle(explicitId)
        : storyIdToTitle(`${layerKey}_story_${i + 1}`);
      storyEntries.push({
        id,
        title: (obj.title as string) || (obj.name as string) || fallbackTitle,
        // Normalize whatever the model returned for `match` into our 3
        // allowed StoryFit values. Without this, an unexpected value
        // (we've seen "high", "great", "moderate", null, …) flows
        // straight into FIT_TONE[fit] -> undefined -> "Cannot read
        // properties of undefined (reading 'bg')" the moment the chip
        // tries to render. Keep accepting unknown synonyms by mapping
        // them to "good" — the AI's intent is "this story fits the
        // company"; the exact strength is decorative.
        fit: normalizeStoryFit(obj.match),
        layer,
      });
    });
  }

  if (storyEntries.length === 0) {
    storyEntries.push({
      id: "pipeline_health_crisis",
      title: storyIdToTitle("pipeline_health_crisis"),
      fit: "good",
      layer: "data",
    });
  }

  const volumes = normalizeVolumes(pack.volumes);
  const finalVolumes: Record<string, VolumeTier> =
    Object.keys(volumes).length > 0
      ? volumes
      : {
          small:  { cases: 5_000,  opportunities: 1_000, leads: 500,   total: 6_500 },
          medium: { cases: 15_000, opportunities: 3_000, leads: 1_000, total: 19_000 },
          large:  { cases: 30_000, opportunities: 5_000, leads: 2_000, total: 37_000 },
          xlarge: { cases: 50_000, opportunities: 8_000, leads: 3_500, total: 61_500 },
        };

  const warnings: string[] = [];
  if (productNames.length === 0) {
    warnings.push("No products detected — review the source or add a few before generating.");
  }
  if (repArchetypes.length < 3) {
    warnings.push(
      "AI emitted fewer than 3 rep archetypes — sales rep behavior won't have full industry flavor. The pipeline normalizer will fill in defaults so generation still runs.",
    );
  }
  if (agentArchetypes.length < 3) {
    warnings.push(
      "AI emitted fewer than 3 service agent archetypes — agent behavior won't have full industry flavor. The pipeline normalizer will fill in defaults.",
    );
  }
  if (orphanRepRefs.length > 0) {
    warnings.push(
      `AI assigned reps to archetype name(s) not defined in rep_archetypes: ${orphanRepRefs.join(", ")}. The normalizer will rewrite these to the nearest defined archetype before generation runs.`,
    );
  }
  if (orphanAgentRefs.length > 0) {
    warnings.push(
      `AI assigned agents to archetype name(s) not defined in agent_archetypes: ${orphanAgentRefs.join(", ")}. The normalizer will rewrite these.`,
    );
  }
  if (caseCategories.length === 0) {
    warnings.push(
      "No industry case_categories emitted — Cases will use generic subjects. Demo will work but lose some industry flavor.",
    );
  }
  if (storyEntries.length < 3) {
    warnings.push(
      "AI suggested fewer than 3 demo stories. The full pack may still contain more — confirm on Review & edit.",
    );
  }

  return {
    name: (company.name as string) || "Untitled company",
    industry: (company.industry as string) || "—",
    tagline: (company.tagline as string) || "",
    brandColor: (company.brand_color as string) || "#0B5CAB",
    icon: (company.icon as string) || "✦",
    logoSource:
      mode === "url"
        ? "Detected from website"
        : mode === "upload"
          ? "From uploaded file"
          : "AI-generated",
    description: (company.description as string) || "",
    archetypes,
    caseCategories,
    products: productNames,
    stories: storyEntries,
    volumes: finalVolumes,
    warnings,
  };
}

export function AIGeneratorSheet(): JSX.Element | null {
  const open = useUiStore((s) => s.aiGenOpen);
  const setOpen = useUiStore((s) => s.setAiGenOpen);
  const showToast = useUiStore((s) => s.showToast);
  const setConfig = useConfigStore((s) => s.setConfig);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [inputMode, setInputMode] = useState<InputMode>("describe");
  const [prompt, setPrompt] = useState("");
  const [url, setUrl] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedName, setUploadedName] = useState("");
  const [phase, setPhase] = useState<Phase>("input");
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [packJson, setPackJson] = useState<PackJson | null>(null);
  const [savedPackId, setSavedPackId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("document");
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(0);
  const [thinkingLabel, setThinkingLabel] = useState("");
  const [thinkingPct, setThinkingPct] = useState(0);
  // Elapsed time during the thinking phase. Backend jobs can take
  // 30s–3min depending on URL scrape latency or LLM contention; the
  // sheet has to make that wait honest and observable.
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const elapsedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setPrompt("");
      setUrl("");
      setUploadedFile(null);
      setUploadedName("");
      setPhase("input");
      setDraft(null);
      setPackJson(null);
      setSavedPackId(null);
      setInputMode("describe");
      setViewMode("document");
      setRefinePrompt("");
      setThinkingStep(0);
      setThinkingLabel("");
      setThinkingPct(0);
      setElapsedMs(0);
      setError(null);
      setSaving(false);
      abortRef.current?.abort();
      abortRef.current = null;
      if (elapsedTimerRef.current !== null) {
        window.clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  if (!open) return null;

  const thinkingSteps = thinkingStepsFor(inputMode, {
    url,
    fileName: uploadedName,
  });
  const sourceLabel =
    inputMode === "url"
      ? prettyHost(url) || "—"
      : inputMode === "upload"
        ? uploadedName || "uploaded file"
        : "your description";

  const canGenerate =
    (inputMode === "describe" && prompt.trim().length > 0) ||
    (inputMode === "url"      && url.trim().length > 0) ||
    (inputMode === "upload"   && uploadedFile != null);

  // Drive the visual checklist forward as the backend job actually moves.
  const advanceChecklist = (pct: number): void => {
    const stepsCount = thinkingSteps.length || 1;
    const target = Math.min(stepsCount, Math.floor((pct / 100) * stepsCount));
    setThinkingStep((cur) => Math.max(cur, target));
  };

  const runGenerate = async (): Promise<void> => {
    setError(null);
    setPhase("thinking");
    setThinkingStep(0);
    setThinkingPct(0);
    setElapsedMs(0);
    setThinkingLabel("Queued — waiting for AI capacity");
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    if (elapsedTimerRef.current !== null) {
      window.clearInterval(elapsedTimerRef.current);
    }
    const startedAt = Date.now();
    elapsedTimerRef.current = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 500);

    const onProgress = (p: { pct: number; label: string }): void => {
      setThinkingLabel(p.label);
      setThinkingPct(p.pct);
      advanceChecklist(p.pct);
    };

    try {
      let pack: PackJson;
      let serverPackId: string | undefined;

      if (inputMode === "describe") {
        pack = await aiApi.generatePack(
          prompt.trim(),
          { autoDetect: true },
          { signal: abortRef.current.signal, onProgress },
        );
      } else if (inputMode === "url") {
        const res = await aiApi.generatePackFromUrl(url.trim(), "", {
          signal: abortRef.current.signal,
          onProgress,
        });
        pack = res.pack;
        serverPackId = res.packId;
      } else {
        if (!uploadedFile) throw new Error("No file selected");
        pack = await aiApi.extractPack(uploadedFile, {
          signal: abortRef.current.signal,
          onProgress,
        });
      }

      setPackJson(pack);
      setSavedPackId(serverPackId ?? null);
      setDraft(packToDraft(pack, inputMode));
      setThinkingStep(thinkingSteps.length);
      setPhase("preview");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Generation failed";
      if (msg !== "Aborted") {
        setError(msg);
        showToast(`AI generation failed: ${msg}`);
      }
      setPhase("input");
    } finally {
      if (elapsedTimerRef.current !== null) {
        window.clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
    }
  };

  const runRefine = async (): Promise<void> => {
    if (!refinePrompt.trim() || !draft || !packJson) return;
    const text = refinePrompt;
    setRefining(true);
    setError(null);
    // F-H1 (2026-04-20): wire the existing abort controller into
    // refinePack so closing the sheet, navigating away, or hitting
    // the explicit Cancel button actually stops the in-flight LLM
    // poll. Previously the request kept polling /api/jobs/* in the
    // background after the user moved on, churning the API and
    // surfacing a stale toast when it eventually settled.
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      const res = await aiApi.refinePack(packJson, text, [], {
        signal: abortRef.current.signal,
      });
      setPackJson(res.pack);
      const next = packToDraft(res.pack, inputMode);
      next.refineLog = [...(draft.refineLog ?? []), { at: "just now", text: res.summary || text }];
      setDraft(next);
      setRefinePrompt("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Refine failed";
      if (msg !== "Aborted") {
        setError(msg);
        showToast(`Refine failed: ${msg}`);
      }
    } finally {
      setRefining(false);
    }
  };

  const ensureSaved = async (): Promise<{ id: string; name: string } | null> => {
    if (!draft || !packJson) return null;
    if (savedPackId) return { id: savedPackId, name: draft.name };
    setSaving(true);
    try {
      const saved = await aiApi.savePackDraft(packJson, draft.name);
      setSavedPackId(saved.id);
      // Invalidate the packs query so Configure / Profiles / Landing
      // see the just-saved draft on their next render. Without this,
      // "Use in Flight Plan" lands on Configure with the new
      // ``profileId`` set but the underlying ``["packs"]`` query
      // still serving its stale cached list — which falls through to
      // ``pack === undefined`` and renders an empty Stage 1.
      void queryClient.invalidateQueries({ queryKey: ["packs"] });
      return { id: saved.id, name: saved.name };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Save failed";
      showToast(`Could not save profile: ${msg}`);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndUse = async (): Promise<void> => {
    const saved = await ensureSaved();
    if (!saved) return;
    setConfig({ mode: "full", profileId: saved.id });
    setOpen(false);
    showToast(`Profile "${saved.name}" loaded into Flight Plan`);
    navigate("/configure");
  };
  const editInPackEditor = async (): Promise<void> => {
    const saved = await ensureSaved();
    if (!saved) return;
    setOpen(false);
    showToast(`Profile "${saved.name}" ready to edit`);
    navigate(`/profiles/${encodeURIComponent(saved.id)}/edit`);
  };
  const saveOnly = async (): Promise<void> => {
    const saved = await ensureSaved();
    if (!saved) return;
    setOpen(false);
    showToast(`Profile saved as "${saved.name}" — draft`);
    navigate("/profiles");
  };

  // C8 (2026-04-20): Backdrop click no longer aborts an in-flight
  // generation. The previous behaviour silently cancelled an LLM
  // request that can take 60-120s — a casual click outside the sheet
  // would discard a half-drafted pack with no warning. Now the
  // backdrop is only dismiss-on-click while idle / preview / error;
  // during the "thinking" phase the user has to use the explicit
  // Cancel button (which gives a clear cancel-confirmation toast).
  const closeOnBackdrop = (): void => {
    if (phase === "thinking") return;
    setOpen(false);
  };

  return (
    <div
      onClick={closeOnBackdrop}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(20,34,56,.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-in-right"
        style={{
          width: 760,
          maxWidth: "95vw",
          height: "100%",
          background: "var(--surface)",
          borderLeft: "1px solid var(--bd)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-24px 0 60px -20px rgba(3,45,96,.4)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid var(--bd-2)",
            background:
              "linear-gradient(135deg, var(--eb-15), var(--eb-50))",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "rgba(255,255,255,.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src="/assets/astro-happy.png"
                alt=""
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "contain",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))",
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                AI Profile Generator
              </div>
              <div style={{ fontSize: 11, color: "#AACBE2" }}>
                Describe · paste a URL · upload JSON — AI drafts the rest.
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "rgba(255,255,255,.1)",
              border: 0,
              color: "white",
              width: 28,
              height: 28,
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 16,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          className="thin-scroll"
          style={{ flex: 1, overflow: "auto", padding: 24 }}
        >
          {phase === "input" && (
            <InputPhase
              inputMode={inputMode}
              setInputMode={setInputMode}
              prompt={prompt}
              setPrompt={setPrompt}
              url={url}
              setUrl={setUrl}
              uploadedName={uploadedName}
              setUploadedName={setUploadedName}
              setUploadedFile={setUploadedFile}
            />
          )}

          {phase === "thinking" && (
            <ThinkingPhase
              inputMode={inputMode}
              source={sourceLabel}
              steps={thinkingSteps}
              current={thinkingStep}
              label={thinkingLabel}
              pct={thinkingPct}
              elapsedMs={elapsedMs}
              onCancel={() => {
                abortRef.current?.abort();
                setPhase("input");
              }}
            />
          )}

          {phase === "preview" && draft && (
            <PreviewPhase
              draft={draft}
              setDraft={setDraft}
              inputMode={inputMode}
              viewMode={viewMode}
              setViewMode={setViewMode}
              refinePrompt={refinePrompt}
              setRefinePrompt={setRefinePrompt}
              refining={refining}
              onRefine={() => { void runRefine(); }}
            />
          )}
          {error && phase === "input" && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                background: "#fdecec",
                border: "1px solid #f5c5c5",
                borderRadius: 8,
                fontSize: 12,
                color: "#7a1d1d",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid var(--bd-2)",
            background: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {phase === "input" && (
            <>
              <button
                onClick={() => setOpen(false)}
                className="btn ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => { void runGenerate(); }}
                disabled={!canGenerate}
                className="btn primary lg"
                style={{ opacity: canGenerate ? 1 : 0.5 }}
              >
                ✦{" "}
                {inputMode === "url"
                  ? "Scan & generate"
                  : inputMode === "upload"
                    ? "Import & enrich"
                    : "Generate profile"}
              </button>
            </>
          )}
          {phase === "thinking" && (
            <div
              style={{
                width: "100%",
                textAlign: "center",
                fontSize: 11,
                color: "var(--ink-3)",
              }}
            >
              {elapsedMs < 60_000
                ? "AI is drafting — usually 30–90 seconds for full company research."
                : elapsedMs < 180_000
                  ? "Still working — large prompts and URL scrapes can take 2–3 minutes."
                  : "Taking longer than usual — keep this open or cancel and retry."}
            </div>
          )}
          {phase === "preview" && (
            <>
              <button
                onClick={() => setPhase("input")}
                className="btn ghost"
              >
                ← Start over
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { void saveOnly(); }}
                  className="btn ghost"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save as draft"}
                </button>
                <button
                  onClick={() => { void saveAndUse(); }}
                  className="btn ghost"
                  disabled={saving}
                >
                  Use in Flight Plan
                </button>
                <button
                  onClick={() => { void editInPackEditor(); }}
                  className="btn primary lg"
                  disabled={saving}
                >
                  Review & edit →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Input ────────────────────────────────────────────────────────
function InputPhase({
  inputMode,
  setInputMode,
  prompt,
  setPrompt,
  url,
  setUrl,
  uploadedName,
  setUploadedName,
  setUploadedFile,
}: {
  inputMode: InputMode;
  setInputMode: (m: InputMode) => void;
  prompt: string;
  setPrompt: (s: string) => void;
  url: string;
  setUrl: (s: string) => void;
  uploadedName: string;
  setUploadedName: (s: string) => void;
  setUploadedFile: (f: File | null) => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <div
        style={{
          display: "inline-flex",
          background: "var(--bg-2)",
          border: "1px solid var(--bd)",
          borderRadius: 10,
          padding: 3,
          gap: 2,
          marginBottom: 18,
        }}
      >
        {[
          { id: "describe", label: "Describe", icon: "✎" },
          { id: "url",      label: "From URL", icon: "⊙" },
          { id: "upload",   label: "Upload JSON", icon: "⇪" },
        ].map((t) => {
          const active = inputMode === (t.id as InputMode);
          return (
            <button
              key={t.id}
              onClick={() => setInputMode(t.id as InputMode)}
              style={{
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                background: active ? "white" : "transparent",
                color: active ? "var(--eb-15)" : "var(--ink-2)",
                border: 0,
                borderRadius: 7,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                boxShadow: active ? "0 1px 2px rgba(0,0,0,.06)" : "none",
              }}
            >
              <span>{t.icon}</span> {t.label}
            </button>
          );
        })}
      </div>

      {inputMode === "describe" && (
        <>
          <div className="label" style={{ marginBottom: 8 }}>
            Describe your target
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A mid-size solar installer in Arizona — residential + commercial, ~200 reps, CEO is tech-forward..."
            rows={5}
            style={{
              width: "100%",
              padding: 14,
              fontSize: 13,
              border: "1px solid var(--bd)",
              borderRadius: 10,
              outline: "none",
              fontFamily: "inherit",
              resize: "vertical",
              background: "white",
            }}
          />
          <div style={{ marginTop: 20 }}>
            <div className="label" style={{ marginBottom: 8 }}>
              Or start from one of these
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  onClick={() => setPrompt(s)}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontSize: 12,
                    color: "var(--ink-2)",
                    background: "white",
                    border: "1px solid var(--bd)",
                    borderRadius: 8,
                    cursor: "pointer",
                    lineHeight: 1.5,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {inputMode === "url" && (
        <>
          <div className="label" style={{ marginBottom: 8 }}>
            Company website
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                background: "white",
                border: "1px solid var(--bd)",
                borderRadius: 10,
                padding: "2px 4px 2px 12px",
              }}
            >
              <span style={{ color: "var(--ink-3)", fontSize: 12 }}>
                https://
              </span>
              <input
                value={url.replace(/^https?:\/\//, "")}
                onChange={(e) => setUrl("https://" + e.target.value)}
                placeholder="heliossolar.com"
                style={{
                  flex: 1,
                  border: 0,
                  padding: "12px 8px",
                  fontSize: 13,
                  outline: "none",
                  background: "transparent",
                }}
              />
            </div>
          </div>
          <div
            style={{
              marginTop: 10,
              padding: 12,
              background: "#F2F8FC",
              border: "1px solid #cce3f6",
              borderRadius: 10,
              fontSize: 12,
              color: "var(--ink-2)",
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: "var(--ink)" }}>AI will:</strong> fetch the
            site and extract the company description, products, personas, and
            stories — then build the profile automatically.
            <div
              style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}
            >
              Logo is pulled from the site for the profile card only — brand
              color isn't used in the dataset.
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <div className="label" style={{ marginBottom: 8 }}>
              Examples
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {URL_SAMPLES.map((s) => (
                <button
                  key={s}
                  onClick={() => setUrl(s)}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontSize: 12,
                    color: "var(--eb-50)",
                    background: "white",
                    border: "1px solid var(--bd)",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontFamily: "ui-monospace, Menlo, monospace",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {inputMode === "upload" && (
        <>
          <div className="label" style={{ marginBottom: 8 }}>
            Upload a profile (PDF or JSON)
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf,application/json,.json"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setUploadedFile(f);
              setUploadedName(f?.name ?? "");
            }}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              padding: 24,
              border: "2px dashed var(--bd)",
              borderRadius: 10,
              background: "white",
              textAlign: "center",
              cursor: "pointer",
              borderColor: uploadedName ? "var(--teal)" : "var(--bd)",
            }}
          >
            {uploadedName ? (
              <>
                <div style={{ fontSize: 28, color: "var(--teal)" }}>✓</div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    marginTop: 8,
                    fontWeight: 600,
                  }}
                >
                  {uploadedName}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    marginTop: 4,
                  }}
                >
                  4.2 KB · valid schema
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadedFile(null);
                    setUploadedName("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="btn ghost sm"
                  style={{ marginTop: 10 }}
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 32, color: "var(--ink-3)" }}>⇪</div>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    marginTop: 8,
                  }}
                >
                  Drop a JSON file or click to pick
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    marginTop: 4,
                  }}
                >
                  Must match the Generator profile schema
                </div>
              </>
            )}
          </div>
          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ flex: 1 }}>
              <div className="label" style={{ marginBottom: 6 }}>
                Optional — upload a logo
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  background: "white",
                  border: "1px dashed var(--bd)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--ink-3)",
                  cursor: "pointer",
                }}
              >
                Pick PNG or SVG · else AI will look it up
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button
              style={{
                background: "transparent",
                border: 0,
                color: "var(--eb-50)",
                fontSize: 12,
                fontWeight: 600,
                padding: 0,
                cursor: "pointer",
              }}
            >
              ↓ Download schema template
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ─── Thinking ─────────────────────────────────────────────────────
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

function ThinkingPhase({
  inputMode,
  source,
  steps,
  current,
  label,
  pct,
  elapsedMs,
  onCancel,
}: {
  inputMode: InputMode;
  source: string;
  steps: string[];
  current: number;
  label: string;
  pct: number;
  elapsedMs: number;
  onCancel: () => void;
}): JSX.Element {
  const queued = label.toLowerCase().includes("queued");
  return (
    <div style={{ padding: "40px 0" }}>
      <div
        style={{
          width: 72,
          height: 72,
          margin: "0 auto 24px",
          borderRadius: 18,
          background:
            "linear-gradient(135deg, var(--eb-15), var(--eb-50))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: 32,
          animation: "aiGenPulse 1.2s ease-in-out infinite",
        }}
      >
        ✦
      </div>
      <style>{`
        @keyframes aiGenPulse {
          0%, 100% { transform: scale(1);    opacity: .9; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
      `}</style>
      <div
        style={{
          textAlign: "center",
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink)",
        }}
      >
        {inputMode === "url"
          ? "Scanning website…"
          : inputMode === "upload"
            ? "Processing your file…"
            : "Generating profile…"}
      </div>
      {source && (
        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            color: "var(--ink-2)",
            marginTop: 6,
            fontFamily:
              inputMode === "url" ? "ui-monospace, Menlo, monospace" : "inherit",
          }}
        >
          {source}
        </div>
      )}
      {label && (
        <div
          style={{
            textAlign: "center",
            fontSize: 11,
            color: "var(--ink-3)",
            marginTop: 6,
          }}
        >
          {queued ? "⏳ " : "✦ "}
          {label}
        </div>
      )}
      <div
        style={{
          maxWidth: 320,
          margin: "12px auto 0",
          height: 6,
          background: "var(--cb-95)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(4, Math.min(100, pct))}%`,
            height: "100%",
            background: queued
              ? "linear-gradient(90deg, var(--ink-3), var(--ink-2))"
              : "linear-gradient(90deg, var(--eb-50), var(--violet))",
            transition: "width .4s ease",
          }}
        />
      </div>
      <div
        style={{
          textAlign: "center",
          marginTop: 6,
          fontSize: 10,
          color: "var(--ink-3)",
          letterSpacing: ".04em",
        }}
      >
        {pct > 0 ? `${pct}% · ` : ""}elapsed {formatElapsed(elapsedMs)}
      </div>
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={onCancel} className="btn ghost sm">
          Cancel
        </button>
      </div>
      <div
        style={{
          maxWidth: 440,
          margin: "28px auto 0",
          display: "grid",
          gap: 8,
        }}
      >
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div
              key={s}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 8,
                background: active ? "var(--cb-95)" : "transparent",
                border: active
                  ? "1px solid var(--cb-90)"
                  : "1px solid transparent",
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  flexShrink: 0,
                  background: done
                    ? "var(--teal)"
                    : active
                      ? "#E8A23F"
                      : "var(--bd)",
                  color: "white",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {done ? "✓" : active ? "◇" : ""}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: done
                    ? "var(--ink-3)"
                    : active
                      ? "var(--ink)"
                      : "var(--ink-3)",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {s}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Preview ──────────────────────────────────────────────────────
function PreviewPhase({
  draft,
  setDraft,
  inputMode,
  viewMode,
  setViewMode,
  refinePrompt,
  setRefinePrompt,
  refining,
  onRefine,
}: {
  draft: ProfileDraft;
  setDraft: (mut: (d: ProfileDraft | null) => ProfileDraft | null) => void;
  inputMode: InputMode;
  viewMode: ViewMode;
  setViewMode: (m: ViewMode) => void;
  refinePrompt: string;
  setRefinePrompt: (s: string) => void;
  refining: boolean;
  onRefine: () => void;
}): JSX.Element {
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            background: "var(--bg-2)",
            border: "1px solid var(--bd)",
            borderRadius: 8,
            padding: 2,
            gap: 2,
          }}
        >
          {(["document", "json"] as const).map((t) => {
            const active = viewMode === t;
            return (
              <button
                key={t}
                onClick={() => setViewMode(t)}
                style={{
                  padding: "5px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  background: active ? "white" : "transparent",
                  color: active ? "var(--eb-15)" : "var(--ink-2)",
                  border: 0,
                  borderRadius: 6,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
          {inputMode === "url" && <>✓ Logo found · ✓ Colors extracted</>}
          {inputMode === "upload" && <>✓ Schema valid · ✓ Enriched</>}
          {inputMode === "describe" && <>✓ Generated from prompt</>}
        </div>
      </div>

      {viewMode === "document" ? (
        <DocumentView draft={draft} setDraft={setDraft} />
      ) : (
        <JsonView draft={draft} />
      )}

      {/* Refine bar */}
      <div
        className="card"
        style={{
          marginTop: 18,
          padding: 14,
          borderColor: "var(--eb-50)",
          background: "#F4F8FC",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--eb-50)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            marginBottom: 8,
          }}
        >
          Refine with AI
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={refinePrompt}
            onChange={(e) => setRefinePrompt(e.target.value)}
            disabled={refining}
            placeholder="e.g. Add a CFO persona · make the volumes bigger · change industry to B2B"
            style={{
              flex: 1,
              padding: "10px 12px",
              fontSize: 12,
              border: "1px solid var(--bd)",
              borderRadius: 8,
              outline: "none",
              background: "white",
            }}
          />
          <button
            onClick={onRefine}
            disabled={!refinePrompt.trim() || refining}
            className="btn primary"
            style={{ opacity: refinePrompt.trim() ? 1 : 0.5 }}
          >
            {refining ? "…" : "Refine →"}
          </button>
        </div>
        {draft.refineLog && draft.refineLog.length > 0 && (
          <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
            {draft.refineLog.map((r, i) => (
              <div
                key={i}
                style={{
                  fontSize: 11,
                  color: "var(--ink-2)",
                  padding: "4px 8px",
                  background: "#EAF8F4",
                  borderLeft: "3px solid var(--teal)",
                  borderRadius: 4,
                }}
              >
                <span style={{ color: "var(--teal)" }}>✓</span> {r.text}{" "}
                <span style={{ color: "var(--ink-3)" }}>· {r.at}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function DocumentView({
  draft,
  setDraft,
}: {
  draft: ProfileDraft;
  setDraft: (mut: (d: ProfileDraft | null) => ProfileDraft | null) => void;
}): JSX.Element {
  const FIT_TONE: Record<
    StoryFit,
    { bg: string; fg: string; label: string }
  > = {
    perfect: { bg: "#E5F4EE", fg: "var(--teal)", label: "Perfect match" },
    good:    { bg: "var(--cb-95)", fg: "var(--eb-50)", label: "Good match" },
    custom:  { bg: "#FCEFD2", fg: "#8a5200", label: "AI-generated" },
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 12,
            background: draft.brandColor,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            flexShrink: 0,
          }}
        >
          {draft.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <input
              value={draft.name}
              onChange={(e) =>
                setDraft((d) => (d ? { ...d, name: e.target.value } : d))
              }
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "var(--ink)",
                border: 0,
                background: "transparent",
                padding: 0,
                outline: "none",
                width: "auto",
                minWidth: 200,
              }}
            />
            <span className="chip ghost" style={{ fontSize: 10 }}>
              Draft · editable
            </span>
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              marginTop: 2,
            }}
          >
            {draft.industry}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              marginTop: 4,
            }}
          >
            Logo: {draft.logoSource}
          </div>
        </div>
      </div>

      <p
        style={{
          fontSize: 13,
          color: "var(--ink-2)",
          lineHeight: 1.6,
          margin: "0 0 20px",
          padding: 14,
          background: "white",
          border: "1px solid var(--bd-2)",
          borderRadius: 10,
        }}
      >
        {draft.description}
      </p>

      <div style={{ marginBottom: 18 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Industry archetypes
          <span
            style={{
              marginLeft: 8,
              fontSize: 10,
              color: "var(--ink-3)",
              fontWeight: 500,
            }}
          >
            ({draft.archetypes.accounts.length} accounts · {draft.archetypes.reps.length} reps · {draft.archetypes.agents.length} agents)
          </span>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <ArchetypeSection
            title="Account archetypes"
            entries={draft.archetypes.accounts}
            kind="account"
          />
          <ArchetypeSection
            title="Sales rep archetypes"
            entries={draft.archetypes.reps}
            kind="rep"
            orphans={draft.archetypes.orphanRepRefs}
          />
          <ArchetypeSection
            title="Service agent archetypes"
            entries={draft.archetypes.agents}
            kind="agent"
            orphans={draft.archetypes.orphanAgentRefs}
          />
          {draft.archetypes.contactTitles.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--eb-50)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                  marginBottom: 6,
                }}
              >
                Decision-maker titles ({draft.archetypes.contactTitles.length})
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {draft.archetypes.contactTitles.map((t) => (
                  <span key={t} className="chip ghost" style={{ fontSize: 11 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {draft.caseCategories.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div className="label" style={{ marginBottom: 8 }}>
            Case categories ({draft.caseCategories.length})
            <span
              style={{
                marginLeft: 8,
                fontSize: 10,
                color: "var(--ink-3)",
                fontWeight: 500,
              }}
            >
              merged into Case.Subject at generation time
            </span>
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {draft.caseCategories.map((c) => (
              <div
                key={c.name}
                style={{
                  padding: 10,
                  background: "white",
                  border: "1px solid var(--bd)",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>
                    {c.label}
                  </span>
                  <span className="chip ghost" style={{ fontSize: 10 }}>
                    {c.caseType}
                  </span>
                  <span style={{ fontSize: 10, color: "var(--ink-3)" }}>
                    {c.subjects.length} subject{c.subjects.length === 1 ? "" : "s"}
                  </span>
                </div>
                {c.description && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    {c.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Products ({draft.products.length})
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {draft.products.map((p) => (
            <span key={p} className="chip ghost" style={{ fontSize: 11 }}>
              {p}
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Suggested demo stories ({draft.stories.length})
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          {draft.stories.map((s) => {
            // Suspenders: even if a stale draft in localStorage has an
            // out-of-enum `fit`, never let it crash the page. Falling
            // through to `good` matches the parser's default.
            const tone = FIT_TONE[s.fit] ?? FIT_TONE.good;
            return (
              <label
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: 10,
                  background: "white",
                  border: "1px solid var(--bd)",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  defaultChecked
                  style={{ accentColor: "var(--eb-50)", marginTop: 2 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--ink)",
                      fontWeight: 600,
                    }}
                  >
                    {s.title}
                  </div>
                  {s.description && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--ink-3)",
                        marginTop: 3,
                        lineHeight: 1.4,
                      }}
                    >
                      {s.description}
                    </div>
                  )}
                </div>
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: tone.bg,
                    color: tone.fg,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    marginTop: 1,
                  }}
                >
                  {tone.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div className="label" style={{ marginBottom: 8 }}>
          Suggested data shape
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.max(1, Object.keys(draft.volumes).length)}, 1fr)`,
            gap: 1,
            background: "var(--bd)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {Object.entries(draft.volumes).map(([tierName, tier]) => (
            <div
              key={tierName}
              style={{
                background: "white",
                padding: "12px 8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--eb-50)",
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                }}
              >
                {tierName}
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--ink)",
                  marginTop: 4,
                  lineHeight: 1.1,
                }}
              >
                {formatCount(tier.total)}
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: "var(--ink-3)",
                  marginTop: 2,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                }}
              >
                records
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  marginTop: 6,
                  lineHeight: 1.4,
                }}
              >
                {formatCount(tier.cases)} cases
                <br />
                {formatCount(tier.opportunities)} opps · {formatCount(tier.leads)} leads
              </div>
            </div>
          ))}
        </div>
      </div>

      {draft.warnings.length > 0 && (
        <div
          style={{
            padding: 12,
            background: "#FEF3D6",
            border: "1px solid #F8DA86",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#8a5200",
              marginBottom: 4,
            }}
          >
            Things to confirm
          </div>
          <ul
            style={{
              margin: 0,
              padding: "0 0 0 18px",
              fontSize: 12,
              color: "var(--ink-2)",
              lineHeight: 1.6,
            }}
          >
            {draft.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function JsonView({ draft }: { draft: ProfileDraft }): JSX.Element {
  const json = JSON.stringify(
    {
      name: draft.name,
      industry: draft.industry,
      tagline: draft.tagline,
      brand: {
        color: draft.brandColor,
        icon: draft.icon,
        logoSource: draft.logoSource,
      },
      description: draft.description,
      archetypes: draft.archetypes,
      caseCategories: draft.caseCategories,
      products: draft.products,
      stories: draft.stories,
      volumes: draft.volumes,
    },
    null,
    2,
  );
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => navigator.clipboard?.writeText(json)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "white",
          border: "1px solid var(--bd)",
          borderRadius: 6,
          padding: "4px 10px",
          fontSize: 11,
          cursor: "pointer",
          color: "var(--ink-2)",
        }}
      >
        Copy
      </button>
      <pre
        style={{
          background: "#0f1e36",
          color: "#d8e3f3",
          padding: 16,
          borderRadius: 10,
          fontSize: 11,
          lineHeight: 1.6,
          overflow: "auto",
          maxHeight: 480,
          fontFamily: "ui-monospace, Menlo, monospace",
          margin: 0,
        }}
      >
        {json}
      </pre>
    </div>
  );
}
